import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { sort } from 'fast-sort';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { ContextData } from '../models/context-data';
import { MenuInfo } from '../models/menu-info';
import { RouteStateService } from './route-state.service';

import { RbacService } from '../../common-api/system-management-api';
import { LogType } from '../../rt-shared/enums';
import { UserTraceLogService } from '../../rt-shared/services';
import { AuditInfoService } from '../../rt-shared/services/auditInfo.service';
import { isNullOrEmpty } from '../utils/common-functions';
import { SystemEnvService } from './system-env.service';
import { UserProfileService } from './user-profile.service';

/**
 * 功能選單服務
 *
 * @export
 * @class MenuService
 */
@Injectable()
export class MenuService {
  menuItems: MenuInfo[] = [];

  private sidenav$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );
  private menus$: BehaviorSubject<MenuInfo[]> = new BehaviorSubject<MenuInfo[]>(
    []
  );
  private activeMenuItem?: MenuInfo;

  constructor(
    private routeStateService: RouteStateService,
    private rbacService: RbacService,
    private translateService: TranslateService,
    private userProfileService: UserProfileService,
    private systemEnvService: SystemEnvService,
    private contextData: ContextData,
    private userTraceLogService: UserTraceLogService,
    private auditInfoService: AuditInfoService
  ) {
    this.initMenuItems().subscribe((results) => {
      this.menuItems = results;
      this.translateMenuLabel(
        this.translateService.currentLang,
        this.menuItems
      );
      this.menus$.next(results);
    });
    this.translateService.onLangChange.subscribe((result: LangChangeEvent) => {
      if (this.menuItems.length > 0) {
        this.translateMenuLabel(result.lang, this.menuItems);
        this.menuItems = [...this.menuItems];
        this.menus$.next(this.menuItems);
      } else {
        this.initMenuItems().subscribe((results) => {
          this.menuItems = results;
          this.translateMenuLabel(result.lang, this.menuItems);
          this.menus$.next(results);
        });
      }
    });
  }

  get currentMenuItem(): MenuInfo | undefined {
    return this.activeMenuItem;
  }

  get menuItems$(): Observable<MenuInfo[]> {
    return this.menus$.asObservable();
  }

  /**
   * 初始化選單資料
   */
  initMenuItems(): Observable<MenuInfo[]> {
    const querySymbolIndex = this.routeStateService.router.url.indexOf('?');
    const pathWithoutQuery =
      querySymbolIndex < 0
        ? this.routeStateService.router.url
        : this.routeStateService.router.url.slice(0, querySymbolIndex);
    const userNo = this.userProfileService.userNo;
    if (isNullOrEmpty(userNo)) {
      return of(this.menuItems);
    }

    const observable = new Observable<MenuInfo[]>((observer) => {
      this.rbacService
        .rbacUserProfileUserNoSystemGroupSystemGroupCodeMenuTreeGet(
          globalThis.sigv.appContext.projectConfig.systemGroupCode,
          userNo!
        )
        .subscribe({
          next: (results) => {
            const menuItems: MenuInfo[] = [];

            // 第一階層(公司)
            for (const companyMenuItem of results) {
              if ((companyMenuItem.systems ?? []).length === 0) {
                continue;
              }

              const companyId = companyMenuItem.companyId;

              const menuItem: MenuInfo = {
                srcLabel: companyMenuItem.companyShortName,
                localLabel: companyMenuItem.companyShortName,
                i18nLabels: [],
                companyId,
              };

              const sysMenuItems: MenuInfo[] = [];

              // 第二階層(系統)
              const systems = sort(companyMenuItem.systems ?? []).asc(
                (x) => x.sequence
              );
              for (const systemMenuItem of systems) {
                const systemId = systemMenuItem.systemId;
                const systemCode = systemMenuItem.systemCode;
                const systemHostUrl = systemMenuItem.systemHostUrl;

                const sysMenuItem: MenuInfo = {
                  srcLabel:
                    systemMenuItem.systemNameEnglish ??
                    systemMenuItem.systemName,
                  localLabel:
                    systemMenuItem.systemNameLocal ?? systemMenuItem.systemName,
                  i18nLabels: systemMenuItem.systemNameOther ?? [],
                  styleClass: 'p-panelmenu-list-level-2',
                  companyId,
                  data: {
                    systemHostUrl,
                    companyId,
                    systemId,
                    systemCode,
                  },
                };

                const funcMenuItems: MenuInfo[] = [];

                // 第三階層(系統功能)
                const sysFunctions = sort(
                  systemMenuItem.systemFunctions ?? []
                ).asc((x) => x.sequence);
                for (const funcMenuItem of sysFunctions) {
                  const systemFunctionId = funcMenuItem.systemFunctionId;
                  const processDefKey = funcMenuItem.processDefKey;
                  const functionCode = funcMenuItem.functionCode;

                  const avaliblePrivilegeItems = sort(
                    funcMenuItem.functionPrivileges ?? []
                  ).asc((x) => x.sequence);

                  if (avaliblePrivilegeItems.length > 0) {
                    const subMenuItem: MenuInfo = {
                      styleClass: 'p-panelmenu-list-level-3',
                      companyId,
                      data: {
                        systemHostUrl,
                        companyId,
                        systemId,
                        functionCode,
                        systemCode,
                      },
                    };

                    const params = {
                      companyId,
                      systemId,
                      systemFunctionId,
                      processDefKey,
                      functionCode,
                    };

                    // 如果底下功能大於一個就展開功能選單，只有一個的話就直接覆蓋系統功能連結
                    if (avaliblePrivilegeItems.length > 1) {
                      const itemMenus: MenuInfo[] = [];
                      subMenuItem.srcLabel =
                        funcMenuItem.functionNameEnglish ??
                        funcMenuItem.functionName;
                      subMenuItem.localLabel =
                        funcMenuItem.functionNameLocal ??
                        funcMenuItem.functionName;

                      // 第四階層(功能)
                      for (const privilegeItem of avaliblePrivilegeItems) {
                        const privilegeCode = privilegeItem.privilegeCode;
                        const itemMenu: MenuInfo = {
                          srcLabel:
                            privilegeItem.privilegeNameEnglish ??
                            privilegeItem.privilegeName,
                          localLabel:
                            privilegeItem.privilegeNameLocal ??
                            privilegeItem.privilegeName,
                          styleClass: 'p-panelmenu-list-level-4',
                          companyId,
                          data: {
                            systemHostUrl,
                            companyId,
                            systemId,
                            functionCode,
                            privilegeCode,
                            systemCode,
                          },
                        };
                        const menuParams = {
                          ...params,
                          privilegeCode,
                        };
                        // 判斷是否為站內 url，是的話走 angular routing，不是的話走一般 url routing
                        if (
                          systemMenuItem.systemHostUrl ===
                          this.systemEnvService.systemInfo.systemHostUrl
                        ) {
                          itemMenu.entryUrl = privilegeItem.entryUrl;
                          itemMenu.command = () => {
                            this.processVitualDomain(
                              systemMenuItem.systemCode ?? ''
                            );
                            this.activeMenuItem = itemMenu;
                            this.routeStateService.navigateTo(
                              privilegeItem.entryUrl!,
                              menuParams
                            );
                            this.auditInfoService.updateAuditInfoByKey(
                              'mode',
                              privilegeItem.privilegeCode || ''
                            );
                            this.auditInfoService.updateAuditInfoByKey(
                              'funcCode',
                              itemMenu.functionCode || ''
                            );
                            this.auditInfoService.updateAuditInfoByKey(
                              'functionPrivilegeId',
                              privilegeItem.functionPrivilegeId || ''
                            );
                          };
                        } else {
                          let queryParams = new HttpParams()
                            .set('lang', this.translateService.currentLang)
                            .set('grantExternalAccess', 'true');
                          Object.entries(menuParams).forEach((entry) => {
                            const key = entry[0];
                            const value = entry[1];
                            queryParams = queryParams.set(
                              key,
                              value?.toString() ?? ''
                            );
                          });
                          itemMenu.entryUrl = itemMenu.url = `${
                            systemMenuItem.systemHostUrl
                          }/#${
                            privilegeItem.entryUrl
                          }?${queryParams.toString()}`;
                        }
                        if (privilegeItem.entryUrl === pathWithoutQuery) {
                          this.activeMenuItem = itemMenu;
                        }
                        itemMenus.push(itemMenu);
                      }
                      subMenuItem.items = itemMenus;
                    } else {
                      const privilegeItem = avaliblePrivilegeItems[0];
                      const privilegeCode = privilegeItem.privilegeCode;
                      subMenuItem.srcLabel =
                        privilegeItem.privilegeNameEnglish ??
                        privilegeItem.privilegeName;
                      subMenuItem.localLabel =
                        privilegeItem.privilegeNameLocal ??
                        privilegeItem.privilegeName;
                      const menuParams = {
                        ...params,
                        privilegeCode,
                      };
                      // 判斷是否為站內 url，是的話走 angular routing，不是的話走一般 url routing
                      if (
                        systemMenuItem.systemHostUrl ===
                        this.systemEnvService.systemInfo.systemHostUrl
                      ) {
                        subMenuItem.entryUrl = privilegeItem.entryUrl;
                        subMenuItem.command = () => {
                          this.processVitualDomain(
                            systemMenuItem.systemCode ?? ''
                          );
                          this.activeMenuItem = subMenuItem;
                          this.routeStateService.navigateTo(
                            privilegeItem.entryUrl!,
                            menuParams
                          );
                        };
                      } else {
                        let queryParams = new HttpParams()
                          .set('lang', this.translateService.currentLang)
                          .set('grantExternalAccess', 'true');
                        Object.entries(menuParams).forEach((entry) => {
                          const key = entry[0];
                          const value = entry[1];
                          queryParams = queryParams.set(
                            key,
                            value?.toString() ?? ''
                          );
                        });
                        subMenuItem.entryUrl = subMenuItem.url = `${
                          systemMenuItem.systemHostUrl
                        }/#${privilegeItem.entryUrl}?${queryParams.toString()}`;
                      }
                      if (privilegeItem.entryUrl === pathWithoutQuery) {
                        this.activeMenuItem = subMenuItem;
                      }
                    }

                    funcMenuItems.push(subMenuItem);
                  }
                }

                if (funcMenuItems.length > 0) {
                  sysMenuItem.items = funcMenuItems;
                }

                sysMenuItems.push(sysMenuItem);
              }

              if (sysMenuItems.length > 0) {
                menuItem.items = sysMenuItems;
              }

              menuItems.push(menuItem);
            }

            observer.next(menuItems);
            observer.complete();
          },
          error: (err) => observer.error(err),
        });
    });

    return observable;
  }

  getBreadCrumbItems(path: string): MenuInfo[] {
    const targetRootMenuItem = this.menuItems.filter(
      (x) => x?.companyId == this.contextData.companyId
    );
    const breadcrumbMenuItems = this.searchBreadCrumbItemsByPath(
      path,
      targetRootMenuItem
    );
    return breadcrumbMenuItems;
  }

  getSideMenuObservable(): Observable<boolean> {
    return this.sidenav$.asObservable();
  }

  toggleSideMenu(): void {
    this.sidenav$.next(!this.sidenav$.value);
  }

  /**
   * Translate menu labels.
   * @param lang 目前語言
   * @param menuItems Target menu items
   */
  translateMenuLabel(lang: string, menuItems: MenuInfo[]): void {
    if (menuItems.length === 0) {
      return;
    }

    menuItems.forEach((x) => {
      try {
        x.label =
          (lang === 'en-us'
            ? x.srcLabel
            : lang === 'km-kh'
            ? this.translateService.instant(x.srcLabel ?? '')
            : x.localLabel) ?? this.translateService.instant(x.srcLabel ?? '');

        if (isNullOrEmpty(x.url) === false) {
          const urlTree = this.routeStateService.router.parseUrl(
            x.url!.replace(x.data?.['systemHostUrl'], '')
          );
          if (urlTree.queryParamMap.has('lang')) {
            urlTree.queryParams['lang'] = lang;
            const routeUrl =
              this.routeStateService.router.serializeUrl(urlTree);
            x.url = `${x.data?.['systemHostUrl']}${routeUrl}`;
          }
        }

        this.translateMenuLabel(lang, x.items ?? ([] as MenuInfo[]));
      } catch (error) {
        this.userTraceLogService.setTraceLog(LogType.BUSINESS_CHECK_END, {
          menuError: x,
        });
      }
    });
  }

  private searchBreadCrumbItemsByPath(
    path: string,
    srcMenuItems: MenuInfo[]
  ): MenuInfo[] {
    if (srcMenuItems.length === 0) {
      return [];
    }
    const querySymbolIndex = path.indexOf('?');
    const pathWithoutQuery =
      querySymbolIndex < 0 ? path : path.slice(0, querySymbolIndex);
    let breadCrumbItems: MenuInfo[] = [];
    for (const item of srcMenuItems) {
      breadCrumbItems = [];
      if (item.entryUrl === pathWithoutQuery) {
        return [
          {
            label: item.label,
            srcLabel: item.srcLabel,
            localLabel: item.localLabel,
            routerLink: item.routerLink,
            entryUrl: item.entryUrl,
            companyId: item.companyId,
          },
        ];
      }
      if ((item.items ?? []).length > 0) {
        const cildrenItems = this.searchBreadCrumbItemsByPath(
          pathWithoutQuery,
          item.items!
        );
        if (cildrenItems.length > 0) {
          return [
            {
              label: item.label,
              srcLabel: item.srcLabel,
              localLabel: item.localLabel,
              routerLink: item.routerLink,
              entryUrl: item.entryUrl,
              companyId: item.companyId,
              command: item.command,
            },
            ...cildrenItems,
          ];
        }
      }
    }
    return breadCrumbItems;
  }

  /**
   * Get menu item subject as observable type.
   *
   * @return {*}  {Observable<MenuItem[]>}
   * @memberof MenuService
   */
  getMenuItemSubject(): Observable<MenuInfo[]> {
    return this.menus$.asObservable();
  }

  processVitualDomain(systemId: string) {
    const useEnvSystemCode = this.systemEnvService.systemInfo.systemCode ?? '';
    const merchantDomains = ['RetailApproval', 'RetailMerchant'];
    const commenceDomains = [
      'RetailCommencementReview',
      'RetailCommencementDisburse',
    ];
    const systemParamDomains = [
      'RetailScheduleSetting',
      'RetailSystemParameter',
    ];
    const hasVirtaulDomain = [
      ...merchantDomains,
      ...commenceDomains,
      ...systemParamDomains,
    ];
    const clsssifyDomain = () => {
      if (merchantDomains.includes(useEnvSystemCode)) {
        return 'merchant';
      }
      if (commenceDomains.includes(useEnvSystemCode)) {
        return 'commence';
      }
      if (systemParamDomains.includes(useEnvSystemCode)) {
        return 'systemParam';
      }
      return '';
    };
    if (hasVirtaulDomain.includes(systemId)) {
      switch (clsssifyDomain()) {
        case 'merchant':
        case 'commence':
        case 'systemParam':
          this.systemEnvService.setVirtualSystemCode(systemId);
          console.log('menu-service');
          return;
        default:
          break;
      }
    }
  }
}
